// Access rules for routes. Each rule takes the `auth` slice state and returns a boolean.
import { ROLES } from '@/entities/user/@x/session';

import type { AuthState } from '../model/authSlice';

export const isAuthenticated = (auth: AuthState) => !!auth.accessToken;

export const isAdmin = (auth: AuthState) =>
  isAuthenticated(auth) && !!auth.isAdmin;

export const isUser = (auth: AuthState) =>
  isAuthenticated(auth) && !!auth.onboarding_finished && !auth.isAdmin;

export const isOnboarding = (auth: AuthState) =>
  isAuthenticated(auth) && !auth.onboarding_finished && !auth.isAdmin;

// Admin should be able to access settings even if onboarding isn't finished.
export const canOpenSettings = (auth: AuthState) =>
  isAuthenticated(auth) && (!!auth.onboarding_finished || !!auth.isAdmin);

// Admins and free users have no subscription to manage.
export const canUseBilling = (auth: AuthState) =>
  !auth.isAdmin && auth.role !== ROLES.FREE_ACCESS;

// Default landing page for the current auth state.
export const getHomePath = (auth: AuthState) => {
  if (!isAuthenticated(auth)) {
    // A sign up waiting for its password goes back to the password step (AU-4-06)
    return auth.pendingSignUpCode
      ? `/sign-up/confirm/${auth.pendingSignUpCode}`
      : '/login';
  }
  if (isAdmin(auth)) return '/admin/customers';
  if (auth.onboarding_finished) return '/dashboard';
  if (auth.email_added) return '/onboarding/plan';

  return '/add-email';
};

// Only a signed-in customer whose account has no email yet (AU-A-04)
export const canAddEmail = (auth: AuthState) =>
  isAuthenticated(auth) && getHomePath(auth) === '/add-email';
